import { useState } from 'react';
import { Plus, Pencil, ScrollText } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Input, Field } from '@/components/ui/Input';
import { SidePanel } from '@/components/ui/SidePanel';
import { Markdown } from '@/components/ui/Markdown';
import { SkeletonRows } from '@/components/ui/Skeleton';
import { ErrorState } from '@/components/ui/States';
import { toast } from '@/components/ui/Toast';
import { LearningsPanel } from '@/components/knowledge/LearningsPanel';
import { useIsStaff } from '@/app/store';
import { useAsync } from '@/lib/useAsync';
import { timeAgo } from '@/lib/timeAgo';
import { api, ApiError, type KnowledgeEntry } from '@/lib/api';
import { useWorkspaceCtx } from './WorkspaceLayout';

/** The engagement knowledge base: charter on top, learnings below (shared panel). */
export function WorkspaceKnowledgeTab() {
  const { workspace } = useWorkspaceCtx();
  const isStaff = useIsStaff();
  const charterQ = useAsync(
    async () => (await api.listKnowledge(workspace.id)).find((e) => e.kind === 'CHARTER') ?? null,
    [workspace.id],
  );
  const [editingCharter, setEditingCharter] = useState(false);

  if (charterQ.status === 'loading') return <SkeletonRows rows={4} />;
  if (charterQ.status === 'error') return <ErrorState error={charterQ.error} onRetry={charterQ.reload} />;
  const charter = charterQ.data;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title={
            <span className="flex items-center gap-2">
              <ScrollText className="h-4 w-4 text-primary" aria-hidden /> {charter?.title ?? 'Engagement charter'}
            </span>
          }
          subtitle={charter ? `Updated ${timeAgo(charter.updatedAt)}` : 'Not written yet.'}
          action={
            isStaff ? (
              charter ? (
                <Button variant="secondary" size="sm" onClick={() => setEditingCharter(true)}>
                  <Pencil className="h-3.5 w-3.5" /> Edit
                </Button>
              ) : (
                <Button size="sm" onClick={() => setEditingCharter(true)}>
                  <Plus className="h-4 w-4" /> Add charter
                </Button>
              )
            ) : undefined
          }
        />
        {charter && (
          <CardBody>
            <Markdown className="text-sm">{charter.body}</Markdown>
          </CardBody>
        )}
      </Card>

      <LearningsPanel
        canEdit={isStaff}
        source={{
          key: `ws-${workspace.id}`,
          list: (archived) => api.listKnowledge(workspace.id, archived).then((es) => es.filter((e) => e.kind === 'LEARNING')),
          create: (body) => api.createKnowledge(workspace.id, { kind: 'LEARNING', ...body }),
          scopeLabel: 'engagement',
          emptyDescription: 'Approved suggestions land here; staff can also author learnings directly.',
        }}
      />

      {editingCharter && (
        <CharterEditor
          workspaceId={workspace.id}
          entry={charter}
          onClose={() => setEditingCharter(false)}
          onSaved={() => {
            setEditingCharter(false);
            charterQ.reload();
          }}
        />
      )}
    </div>
  );
}

function CharterEditor({
  workspaceId,
  entry,
  onClose,
  onSaved,
}: {
  workspaceId: string;
  entry: KnowledgeEntry | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [title, setTitle] = useState(entry?.title ?? 'Engagement charter');
  const [body, setBody] = useState(entry?.body ?? '');
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      if (entry) await api.updateKnowledge(entry.id, { title: title.trim(), body });
      else await api.createKnowledge(workspaceId, { kind: 'CHARTER', title: title.trim(), body });
      toast.success('Charter saved');
      onSaved();
    } catch (err) {
      toast.danger('Could not save the charter', err instanceof ApiError ? err.message : undefined);
      setBusy(false);
    }
  }

  return (
    <SidePanel title={entry ? 'Edit charter' : 'New charter'} onClose={onClose}>
      <form className="space-y-4" onSubmit={submit}>
        <Field label="Title" htmlFor="ch-title">
          <Input id="ch-title" value={title} onChange={(e) => setTitle(e.target.value)} required autoFocus />
        </Field>
        <Field label="Body" htmlFor="ch-body" hint="Markdown. Agents read this verbatim on every run.">
          <textarea
            id="ch-body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={16}
            required
            className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm leading-relaxed text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          />
        </Field>
        <Button type="submit" disabled={busy || !title.trim() || !body.trim()}>
          {busy ? 'Saving…' : 'Save'}
        </Button>
      </form>
    </SidePanel>
  );
}
